import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { eventApiMessage } from '@/components/events/event-errors'
import { deleteJSON, postJSON } from '@/lib/api'

export interface EventRegistrationQuestion {
  id: number
  prompt: string
  kind: QuestionKind
  scope: 'per_order' | 'per_attendee'
  required: boolean
  active: boolean
  options: string[]
}

type QuestionKind = 'short_text' | 'long_text' | 'single_choice' | 'multiple_choice' | 'yes_no'
const kindLabels: Record<QuestionKind, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  yes_no: 'Yes/no',
}

export function EventRegistrationQuestions({
  eventId,
  questions,
}: {
  eventId: number
  questions: EventRegistrationQuestion[]
}) {
  const [adding, setAdding] = React.useState(false)
  const [removing, setRemoving] = React.useState<EventRegistrationQuestion | null>(null)
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: (question: EventRegistrationQuestion) =>
      deleteJSON(`/api/v1/events/${eventId}/registration_questions/${question.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] })
      setRemoving(null)
    },
  })
  const activeQuestions = questions.filter((question) => question.active)

  return (
    <section>
      <h2 className="mb-2 text-caption font-medium text-muted-foreground">Questions</h2>
      <div className="divide-y divide-border rounded-md border border-border bg-card">
        {activeQuestions.map((question) => (
          <div key={question.id} className="flex items-start gap-3 px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-body-sm font-medium">{question.prompt}</p>
              <p className="text-caption text-muted-foreground">
                {kindLabels[question.kind]} · {question.required ? 'Required' : 'Optional'} · once
                {question.scope === 'per_attendee' ? ' per attendee' : ' per order'}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              aria-label={`Remove ${question.prompt}`}
              className="h-8 w-8 border-transparent p-0 text-muted-foreground"
              onClick={() => setRemoving(question)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        ))}
        {adding && <QuestionForm eventId={eventId} onClose={() => setAdding(false)} />}
        <div className="px-4 py-3">
          <Button
            type="button"
            variant="outline"
            className="h-7 border-dashed px-2 text-caption text-muted-foreground"
            onClick={() => setAdding(true)}
          >
            + Add question
          </Button>
        </div>
      </div>
      {remove.error && <Alert className="mt-2">{eventApiMessage(remove.error)}</Alert>}
      <Dialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove question?</DialogTitle>
            <DialogDescription>
              It will stop appearing at checkout. Answers already captured on Orders remain visible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRemoving(null)}>
              Keep question
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!removing || remove.isPending}
              onClick={() => removing && remove.mutate(removing)}
            >
              {remove.isPending ? 'Removing…' : 'Remove question'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

function QuestionForm({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [prompt, setPrompt] = React.useState('')
  const [kind, setKind] = React.useState<QuestionKind>('short_text')
  const [scope, setScope] = React.useState<'per_order' | 'per_attendee'>('per_order')
  const [optionText, setOptionText] = React.useState('')
  const [required, setRequired] = React.useState(false)
  const idempotencyKey = React.useRef(crypto.randomUUID())
  const save = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/events/${eventId}/registration_questions`, {
        registration_question: {
          prompt,
          kind,
          scope,
          required,
          options: ['single_choice', 'multiple_choice'].includes(kind)
            ? optionText.split('\n').map((option) => option.trim())
            : [],
          idempotency_key: idempotencyKey.current,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] })
      onClose()
    },
  })

  return (
    <form
      className="grid gap-3 bg-muted/30 px-4 py-4"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="event-registration-question" className="text-caption text-muted-foreground">
          Question
        </Label>
        <Input
          id="event-registration-question"
          className="h-8"
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-caption text-muted-foreground">Answer type</Label>
        <Select value={kind} onValueChange={(value) => setKind(value as QuestionKind)}>
          <SelectTrigger className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(kindLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="event-question-scope" className="text-caption text-muted-foreground">
          Ask
        </Label>
        <Select
          value={scope}
          onValueChange={(value) => setScope(value as 'per_order' | 'per_attendee')}
        >
          <SelectTrigger id="event-question-scope" className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="per_order">Once per order</SelectItem>
            <SelectItem value="per_attendee">Once per attendee</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {['single_choice', 'multiple_choice'].includes(kind) && (
        <div className="space-y-1">
          <Label htmlFor="event-question-options" className="text-caption text-muted-foreground">
            Options · one per line
          </Label>
          <Textarea
            id="event-question-options"
            rows={3}
            value={optionText}
            onChange={(event) => setOptionText(event.target.value)}
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        <Checkbox
          id="event-question-required"
          checked={required}
          onCheckedChange={(value) => setRequired(value === true)}
        />
        <Label htmlFor="event-question-required" className="text-caption text-muted-foreground">
          Required
        </Label>
      </div>
      {save.error && <Alert>{eventApiMessage(save.error)}</Alert>}
      <div className="flex gap-1.5">
        <Button type="submit" variant="outline" className="h-8 px-3" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save question'}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-8 border-transparent px-3"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
